import React, { useId } from 'react';
import Plot from 'react-plotly.js';
import type { Categoria, ThreadLista } from '../api/types';
import type { Competenza } from '../api/auto-types';
import { COMPETENZA_COLORS, themeColors } from '../globals';

interface Props {
  threads: ThreadLista[];
  categories: Categoria[];
  skills: Competenza[];
  requiredCorrections: number;
  modified: boolean;
  savedVersion: number;
  selectedCount?: number;
}

const isRating = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);
const average = (values: number[]) => values.length ? (values.reduce((sum, value) => sum + value, 0) / values.length).toFixed(2) : '—';

const StatisticheShortlist = ({ threads, categories, skills, requiredCorrections, modified, savedVersion, selectedCount = threads.length }: Props) => {
  const id = useId();
  const beauty = threads.map(thread => thread.bellezza_medio).filter(isRating);
  const difficulty = threads.map(thread => thread.difficolta_medio).filter(isRating);
  const pending = threads.filter(thread => thread.problema.correttori_eseguito.length < requiredCorrections).length;
  const complete = threads.length === selectedCount;
  const categoryCounts = categories.map(category => threads.reduce((sum, thread) =>
    sum + (thread.problema.categoria.includes(category.id) ? 1 / thread.problema.categoria.length : 0), 0));
  const skillCounts = skills.map(skill => threads.filter(thread => thread.problema.competenze?.includes(skill.id)).length);
  const config = { responsive: true, displaylogo: false };
  const layout = { autosize: true, paper_bgcolor: 'transparent', plot_bgcolor: 'transparent',
    font: { family: 'inherit', size: 12, color: '#334155' } };

  return <section className="shortlist-section shortlist-statistics" aria-labelledby={`${id}-heading`}>
    <div className="shortlist-section-heading">
      <h2 id={`${id}-heading`}>Statistiche della selezione</h2>
      <span className={`small ${modified ? 'text-warning' : 'text-muted'}`}>
        {modified ? 'Selezione corrente · modifiche non salvate' : `Versione salvata #${savedVersion}`}
      </span>
    </div>
    {!complete && <p className="text-warning small">Alcuni problemi non sono disponibili: le statistiche non sono complete.</p>}
    <dl className="shortlist-metrics">
      <div><dt>Problemi selezionati</dt><dd>{selectedCount}</dd><small>nell’ordine corrente</small></div>
      <div><dt>Correzioni completate</dt><dd>{complete ? `${selectedCount - pending} / ${selectedCount}` : '—'}</dd>
        <small className={complete && pending ? 'text-danger' : ''}>{!complete ? 'dati incompleti' : !selectedCount ? 'nessun problema selezionato'
          : pending ? `${pending} problemi ancora da correggere` : 'tutti i problemi corretti'} · soglia {requiredCorrections}</small>
      </div>
      <div><dt>Bellezza media</dt><dd>{average(beauty)}</dd><small>{beauty.length} / {selectedCount} problemi con valutazione</small></div>
      <div><dt>Difficoltà media</dt><dd>{average(difficulty)}</dd><small>{difficulty.length} / {selectedCount} problemi con valutazione</small></div>
    </dl>
    <div className="shortlist-chart-grid">
      <figure className="shortlist-chart" aria-labelledby={`${id}-ratings`}>
        <figcaption id={`${id}-ratings`}>Distribuzione delle valutazioni</figcaption>
        <Plot useResizeHandler style={{ width: '100%' }} config={config}
          data={[
            { x: beauty, xbins: { start: 1, end: 5, size: .5 }, type: 'histogram', name: 'Bellezza media', marker: { color: '#3b82f6' } },
            { x: difficulty, xbins: { start: 1, end: 5, size: .5 }, type: 'histogram', name: 'Difficoltà media', marker: { color: '#f59e0b' } },
          ]}
          layout={{ ...layout, height: 320, barmode: 'group', margin: { l: 45, r: 15, t: 35, b: 50 },
            legend: { orientation: 'h', x: 0, y: 1.15 }, xaxis: { title: 'Valutazione', dtick: .5, automargin: true },
            yaxis: { title: 'Problemi', dtick: 1, rangemode: 'tozero', automargin: true } }} />
      </figure>
      <figure className="shortlist-chart" aria-labelledby={`${id}-categories`}>
        <figcaption id={`${id}-categories`}>Distribuzione delle categorie</figcaption>
        <Plot useResizeHandler style={{ width: '100%' }} config={config}
          data={[{ values: categoryCounts, labels: categories.map(category => category.nome), type: 'pie', sort: false,
            name: 'Distribuzione categorie', marker: { colors: categories.map(category => themeColors[category.colore] || '#94a3b8') },
            texttemplate: '%{value:.2f}<br>%{percent}', hovertemplate: '%{label}: %{value:.2f} problemi equivalenti (%{percent})<extra></extra>' }]}
          layout={{ ...layout, height: 320, margin: { l: 10, r: 10, t: 10, b: 45 }, legend: { orientation: 'h', x: 0, y: -.05 } }} />
        <p className="shortlist-chart-note">Ogni problema contribuisce in parti uguali alle proprie categorie.</p>
      </figure>
      <figure className="shortlist-chart shortlist-chart-skills" aria-labelledby={`${id}-skills`}>
        <figcaption id={`${id}-skills`}>Competenze richieste</figcaption>
        <p className="shortlist-chart-note">Numero di problemi per competenza. Un problema può richiederne più di una.</p>
        <Plot useResizeHandler style={{ width: '100%' }} config={config}
          data={[{ x: skillCounts, y: skills.map(skill => skill.nome), type: 'bar', orientation: 'h',
            name: 'Competenze richieste', text: skillCounts.map(String), textposition: 'auto',
            marker: { color: skills.map(skill => themeColors[COMPETENZA_COLORS[skill.tipologia]] || '#94a3b8') },
            hovertemplate: '%{y}: %{x} problemi<extra></extra>' }]}
          layout={{ ...layout, height: Math.max(280, skills.length * 26 + 80), showlegend: false,
            margin: { l: 15, r: 25, t: 5, b: 50 }, xaxis: { title: 'Problemi', dtick: 1, rangemode: 'tozero', automargin: true },
            yaxis: { autorange: 'reversed', automargin: true, tickmode: 'array', tickvals: skills.map(skill => skill.nome) } }} />
      </figure>
    </div>
  </section>;
};

export default StatisticheShortlist;
